import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { lkAuth, ApiError } from "@flame/shared/api";
import { Button, toast } from "@flame/shared/ui";

/**
 * Landing for the "🔑 Открыть личный кабинет" deeplink the bot sends after
 * /start <invite_code> succeeds. URL shape: lk.flmv.ru/auth/bot?token=<onetime>
 *
 * Flow:
 *  1. Read token from query string.
 *  2. POST /v1/lk/auth/bot-session-exchange → burn token, set session cookie.
 *  3. Invalidate react-query caches so the dashboard shows the fresh subscription.
 *  4. Redirect to "/" (or /totp/verify if 2FA is required).
 *
 * Errors:
 *  - 410 token expired → toast + redirect to /login (user re-requests deeplink).
 *  - 409 mismatch     → toast + /login (user opened admin link on LK or vice versa).
 *  - any other        → toast + retry button (manual).
 *
 * Public route — no RequireAuth wrapper. The cookie this page sets IS the
 * authenticated state we hand off to the rest of the app.
 */
export function AuthBotPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const ranRef = useRef(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const token = params.get("token");

  useEffect(() => {
    // Strict-mode double-invoke guard (token is single-use; second call 410s).
    if (ranRef.current) return;
    ranRef.current = true;

    if (!token) {
      setErrorMsg("Не передан токен входа");
      return;
    }

    (async () => {
      try {
        const res = await lkAuth.botSessionExchange(token);
        await queryClient.invalidateQueries();
        toast.success("Вход выполнен", "Подписка готова к использованию");
        if (res.totp_required) {
          navigate("/totp/verify", { replace: true });
        } else {
          navigate("/", { replace: true });
        }
      } catch (err) {
        if (err instanceof ApiError) {
          if (err.status === 410) {
            setErrorMsg("Ссылка устарела. Запустите /start <код> в боте ещё раз.");
          } else if (err.status === 404) {
            setErrorMsg("Токен не найден или уже использован.");
          } else if (err.status === 403) {
            setErrorMsg("Доступ запрещён.");
          } else {
            setErrorMsg(err.message || "Не удалось завершить вход");
          }
        } else {
          setErrorMsg("Сетевая ошибка — проверьте подключение");
        }
      }
    })();
  }, [token, navigate, queryClient]);

  if (errorMsg) {
    return (
      <div className="mx-auto flex min-h-screen max-w-sm flex-col items-center justify-center gap-5 px-4 text-center">
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[var(--color-danger-muted)]">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden>
            <path
              d="M12 8v5m0 3v.01M12 3l9 16H3l9-16z"
              stroke="var(--color-danger)"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        <div>
          <p className="text-base font-semibold text-fg">Не удалось войти</p>
          <p className="mt-1 text-sm text-fg-muted">{errorMsg}</p>
        </div>
        <Button
          variant="primary"
          size="lg"
          onClick={() => void navigate("/login", { replace: true })}
        >
          На экран входа
        </Button>
      </div>
    );
  }

  return (
    <div className="mx-auto flex min-h-screen max-w-sm flex-col items-center justify-center gap-4 px-4 text-center">
      <div
        className="h-10 w-10 animate-spin rounded-full border-2 border-accent border-r-transparent"
        aria-hidden
      />
      <p className="text-sm text-fg-muted">Открываем личный кабинет…</p>
    </div>
  );
}
